/**
 * Request body for generateComparisonPageAsync API
 */
export interface RequestBody {
    /** 陈述书全文 */
    statement_document: string;
    /** 任务ID（可选） */
    task_id?: string;
    /** 专利文本（权利要求或说明书） */
    patent_text?: string;
    /** 专利文本类型 (claim | description) */
    field: 'claim' | 'description';
}

/**
 * Combined request parameters for generateComparisonPageAsync
 */
export interface GenerateComparisonPageAsyncParams {
    body: RequestBody;
}

/**
 * Path parameters for comparison page task APIs
 */
export interface ComparisonPageTaskPathParams {
    /** 异步任务ID */
    async_task_id: string;
}

/**
 * Combined request parameters for status and result APIs
 */
export interface ComparisonPageTaskStatusParams {
    path: ComparisonPageTaskPathParams;
}

/**
 * Combined request parameters for cancel API
 */
export interface CancelComparisonPageTaskParams {
    path: ComparisonPageTaskPathParams;
}

/**
 * Response data for generateComparisonPageAsync
 */
export interface GenerateComparisonPageAsyncResponseData {
    /** 异步任务ID */
    async_task_id: string;
    /** 任务状态 */
    status: string;
    /** 提示消息 */
    message: string;
    /** 任务状态查询URL */
    status_url: string;
    /** 任务结果获取URL */
    result_url: string;
}

/**
 * Response data for getComparisonPageTaskStatus
 */
export interface ComparisonPageTaskStatusResponseData {
    /** 异步任务ID */
    async_task_id: string;
    /** OAR任务ID */
    task_id?: string;
    /** 对照页字段类型 */
    field: 'claim' | 'description';
    /** 任务状态 */
    status: string;
    /** 任务状态描述 */
    status_description: string;
    /** 任务创建时间 */
    create_time: string;
    /** 任务更新时间 */
    update_time: string;
    /** 任务执行时长（秒） */
    duration_in_seconds?: number;
    /** 错误信息 */
    error_message?: string;
    /** 任务是否已结束 */
    is_finished: boolean;
    /** 任务是否可以被取消 */
    is_cancellable: boolean;
    /** 任务是否成功完成 */
    is_successful: boolean;
    /** 任务结果获取URL */
    result_url?: string;
}

/**
 * Response data for getComparisonPageTaskResult
 */
export interface ComparisonPageTaskResultResponseData {
    /** 生成的对照页内容 */
    content: string;
    /** 生成时间戳（毫秒） */
    generated_at?: number;
    /** 内容字符数 */
    word_count?: number;
}

/**
 * API interface for Comparison Page Task operations
 */
export interface ComparisonPageTaskApi {
    /**
   * 异步生成对照页内容
   *
   * 提交异步任务生成对照页内容，立即返回任务ID，避免长时间等待。
   * 支持权利要求对照页和说明书对照页的生成。
   *
   * @param params - Request parameters including request body
   * @returns Promise resolving to task submission response
   */
    generateComparisonPageAsync(params: GenerateComparisonPageAsyncParams): Promise<GenerateComparisonPageAsyncResponseData>;

    /**
   * 查询对照页生成任务状态
   *
   * 根据 asyncTaskId 查询对照页生成任务的当前状态和进度
   *
   * @param params - Request parameters including asyncTaskId in path
   * @returns Promise resolving to task status response
   */
    getComparisonPageTaskStatus(params: ComparisonPageTaskStatusParams): Promise<ComparisonPageTaskStatusResponseData>;

    /**
   * 获取对照页生成任务结果
   *
   * 当任务状态为 COMPLETED 时，获取对照页内容的完整结果
   *
   * @param params - Request parameters including asyncTaskId in path
   * @returns Promise resolving to task result response
   */
    getComparisonPageTaskResult(params: ComparisonPageTaskStatusParams): Promise<ComparisonPageTaskResultResponseData>;

    /**
   * 取消对照页生成任务
   *
   * 取消尚未执行的对照页生成任务（仅 PENDING 状态可取消）
   *
   * @param params - Request parameters including asyncTaskId in path
   * @returns Promise resolving to void response
   */
    cancelComparisonPageTask(params: CancelComparisonPageTaskParams): Promise<void>;
}

/**
 * Symbol for dependency injection
 */
export const ComparisonPageTaskApi = Symbol.for('ComparisonPageTaskApi');
